import { create } from 'zustand';
import { pickLine, type ChatterLevel, type Locale, type PersonaEvent } from '@strategy4kids/persona';
import { speakAs } from '../services/speech.js';
import i18n from 'i18next';
import { parseQuery } from '../lib/query.js';
import { effectiveAudio, useAudioSettingsStore } from './audio-settings-store.js';
import { useProfileStore } from './profile-store.js';
import { CARD_TABLE_SEATS, personaForSeat } from './seat-personas.js';

/**
 * Table chatter (uplift slice U7, docs/proposals/ui-uplift-and-alive-tables.md D7): what the robots
 * at a card table say, one small bubble per seat. The rules from the personas spec hold here:
 * authored lines only, the TEXT is always the source of truth (speech is an optional extra), the
 * chatter level and the parent locks decide how much is said, and only public facts are ever
 * mentioned (who won a trick, who scored best) — never a hidden hand. The human seat never speaks.
 */

export const BUBBLE_MS = 4000;

/** Seat order for the four-seat trick games (matches `BRIDGE_SEATS`); the card-table games use the default. */
export const CHAT_BRIDGE: SayOptions = { seats: ['N', 'E', 'S', 'W'] };

/** The two seats of a partnership. */
export const SIDE_SEATS: Readonly<Record<'NS' | 'EW', readonly string[]>> = { NS: ['N', 'S'], EW: ['E', 'W'] };
export const HUMAN_SEAT = 'S';

export interface SeatBubble {
  readonly line: string;
  /** Changes with every new bubble, so a repeated line still re-renders and re-times. */
  readonly id: number;
}

/**
 * How often each event may speak at each chatter level (0..1). Game results and the opening line
 * always speak when chatter is on; a single trick is rare; a finished round is common.
 */
export function speakChance(level: ChatterLevel, event: PersonaEvent): number {
  if (level === 'off') return 0;
  switch (event) {
    case 'trick-win':
    case 'trick-lose':
      return level === 'lot' ? 0.3 : 0.08;
    case 'round-win':
    case 'round-lose':
      return level === 'lot' ? 1 : 0.5;
    default:
      return 1;
  }
}

/** Trick and round lines count against the per-game cap; the opening and closing lines do not. */
export function countsTowardCap(event: PersonaEvent): boolean {
  return event.startsWith('trick-') || event.startsWith('round-');
}

/** The winner of the trick that was just swept: the table's last completed trick, when one was added. */
export function sweptWinner(
  before: { readonly tricks: { readonly length: number } } | null | undefined,
  after: { readonly trick: { readonly length: number }; readonly tricks: readonly { readonly winner: string }[] },
): string | null {
  if (!before || after.trick.length !== 0 || after.tricks.length <= before.tricks.length) return null;
  return after.tricks[after.tricks.length - 1]?.winner ?? null;
}

/**
 * The robot seat that did best / worst among `scores` (lower is better when `lowIsGood`), counting
 * only the seats in `robots`. Null when it is a tie for that extreme, or nobody is eligible.
 */
export function extremeSeat(
  scores: Readonly<Record<string, number>>,
  robots: readonly string[],
  which: 'best' | 'worst',
  lowIsGood = false,
): string | null {
  const all = Object.keys(scores);
  if (all.length === 0) return null;
  const values = all.map((s) => scores[s] as number);
  const target = (which === 'best') === lowIsGood ? Math.min(...values) : Math.max(...values);
  const holders = all.filter((s) => scores[s] === target);
  if (holders.length !== 1) return null;
  const only = holders[0] as string;
  return robots.includes(only) ? only : null;
}

interface TableChatterState {
  readonly bubbles: Readonly<Record<string, SeatBubble | undefined>>;
  /** Trick / round lines spoken this game (the over-use limiter). */
  readonly spoken: number;
  /** Recent lines per persona (anti-repeat), session memory. */
  readonly recent: Readonly<Record<string, readonly string[]>>;
  /**
   * Try to make `seat` say something for `event`. Returns the line shown, or null when quiet
   * (chatter off, over the cap, the dice said no, someone else is already talking, human seat).
   * `personaId` defaults to the character at that seat.
   */
  say(seat: string, event: PersonaEvent, opts?: SayOptions): string | null;
  clear(seat?: string): void;
  /** A new game: the limiter and every bubble start fresh (the anti-repeat memory is kept). */
  reset(): void;
}

export interface SayOptions {
  readonly personaId?: string;
  /** The table's seat order, so the default persona matches the screen's. */
  readonly seats?: readonly string[];
  readonly locale?: Locale;
  readonly random?: () => number;
}

const timers = new Map<string, ReturnType<typeof setTimeout>>();
let nextId = 1;

/** Screen audits and the scripted-engine e2e surface keep pixels deterministic: no bubbles (as RobotBubble). */
export function chatterSuppressed(): boolean {
  if (import.meta.env.AUDIT_CAPTURE === '1') return true;
  return parseQuery().engine === 'fake';
}

function currentLocale(): Locale {
  const l = (i18n.language || 'en').slice(0, 2);
  return l === 'fr' || l === 'es' ? l : 'en';
}

function speakAloud(personaId: string, line: string, locale: Locale): void {
  const eff = effectiveAudio(useAudioSettingsStore.getState());
  if (!eff.voiceOn || eff.muted) return;
  const token = useProfileStore.getState().profile?.token;
  if (!token) return;
  void speakAs(personaId, line, locale, { token }).catch((err: unknown) => {
    console.warn('[table-chatter] speech failed', err);
  });
}

export const useTableChatter = create<TableChatterState>((set, get) => ({
  bubbles: {},
  spoken: 0,
  recent: {},
  say(seat, event, opts = {}) {
    if (seat === HUMAN_SEAT || chatterSuppressed()) return null;
    const chatter = effectiveAudio(useAudioSettingsStore.getState()).chatter;
    if (chatter === 'off') return null;
    const state = get();
    // One speaker at a time: a seat that is already talking holds the floor.
    if (Object.values(state.bubbles).some(Boolean)) return null;
    const random = opts.random ?? Math.random;
    if (random() >= speakChance(chatter, event)) return null;
    const personaId = opts.personaId ?? personaForSeat(seat, opts.seats ?? CARD_TABLE_SEATS);
    if (!personaId) return null;
    const locale = opts.locale ?? currentLocale();
    const capped = countsTowardCap(event);
    const line = pickLine(
      { personaId, event, locale, spoken: capped ? state.spoken : 0, chatter, recent: state.recent[personaId] ?? [] },
      random,
    );
    if (line === null) return null;
    const bubble: SeatBubble = { line, id: nextId++ };
    set({
      bubbles: { ...state.bubbles, [seat]: bubble },
      spoken: capped ? state.spoken + 1 : state.spoken,
      recent: { ...state.recent, [personaId]: [line, ...(state.recent[personaId] ?? [])].slice(0, 6) },
    });
    const old = timers.get(seat);
    if (old) clearTimeout(old);
    timers.set(
      seat,
      setTimeout(() => {
        timers.delete(seat);
        if (get().bubbles[seat]?.id === bubble.id) get().clear(seat);
      }, BUBBLE_MS),
    );
    speakAloud(personaId, line, locale);
    return line;
  },
  clear(seat) {
    if (seat === undefined) {
      for (const t of timers.values()) clearTimeout(t);
      timers.clear();
      set({ bubbles: {} });
      return;
    }
    const t = timers.get(seat);
    if (t) clearTimeout(t);
    timers.delete(seat);
    const { [seat]: _gone, ...rest } = get().bubbles;
    set({ bubbles: rest });
  },
  reset() {
    get().clear();
    set({ spoken: 0 });
  },
}));

/** Every seat's current bubble, for a screen to hand to its seat plates (`bubbles[seat]?.line`). */
export function useSeatBubbles(): Readonly<Record<string, SeatBubble | undefined>> {
  return useTableChatter((s) => s.bubbles);
}

/** Say something from the store side, silently ignoring errors (chatter must never break a game). */
export function chat(seat: string | null | undefined, event: PersonaEvent, opts?: SayOptions): void {
  if (!seat) return;
  try {
    useTableChatter.getState().say(seat, event, opts);
  } catch (err) {
    console.warn('[table-chatter] say failed', err);
  }
}

/** The robots at a table: every seat of the table except the human's. */
export function robotsOf(seats: readonly string[]): string[] {
  return seats.filter((s) => s !== HUMAN_SEAT);
}

/**
 * A trick was just swept: a robot that took it may say so (`trick-win`); otherwise a robot that
 * played into it and lost may praise the winner (`trick-lose`). Public facts only (the completed
 * trick's winner). The per-level chance keeps it rare.
 */
export function chatTrick(
  before: { readonly tricks: { readonly length: number } } | null | undefined,
  after: { readonly trick: { readonly length: number }; readonly tricks: readonly { readonly winner: string; readonly plays: readonly { readonly seat: string }[] }[] },
  robots: readonly string[],
  opts?: SayOptions,
): void {
  const winner = sweptWinner(before, after);
  if (!winner) return;
  if (robots.includes(winner)) {
    chat(winner, 'trick-win', opts);
    return;
  }
  const last = after.tricks[after.tricks.length - 1];
  const losers = robots.filter((r) => last?.plays.some((p) => p.seat === r));
  const pool = losers.length > 0 ? losers : [...robots];
  chat(pool.length > 0 ? pool[after.tricks.length % pool.length] : null, 'trick-lose', opts);
}

/** A round ended (not the game): the robot that did best says `round-win`, else the one that did worst `round-lose`. */
export function chatRound(best: string | null, worst: string | null, opts?: SayOptions): void {
  if (best) chat(best, 'round-win', opts);
  else if (worst) chat(worst, 'round-lose', opts);
}

/**
 * The game ended: a robot among the winners says `win`; otherwise one of the robots that did not
 * win says `lose` (kindly — the authored lines are generous in defeat).
 */
export function chatGameEnd(winners: readonly string[], robots: readonly string[], opts?: SayOptions): void {
  const winner = robots.find((r) => winners.includes(r));
  if (winner) {
    chat(winner, 'win', opts);
    return;
  }
  chat(robots.find((r) => !winners.includes(r)), 'lose', opts);
}

/** A new game at the table: bubbles and the limiter start fresh. */
export function chatterNewGame(): void {
  useTableChatter.getState().reset();
}
